import {
  Card,
  Table,
  AdvancedFilterColumn,
  confirmDialog,
} from "@velocity-ehs/component-library";
import { Column } from "primereact/column";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { PageInfo } from "../../../supportFunctions/TableReducer";
import {
  ExplicitAssignedOptions,
  LazyParams,
  UserAssignedToRole,
  UserStatusOptions,
} from "../Utils";
import classNames from "classnames/bind";
import classes from "./StatusBody.module.scss";
import * as Permissions from "../../../supportFunctions/Permissions";
import UsersService from "@velocity-ehs/user-utils-module";
import useLocationsRolesMutation, {
  AssignLocationRolesInput,
  LocationRolesActions,
  RevokeLocationRolesInput,
} from "../../../../hooks/useLocationRolesMutation";
import { LocationRoleAssignSlider } from "./LocationRoleAssignSlider";
import { useQueryClient } from "@tanstack/react-query";

interface UsersAssignedToRoleListProps {
  users: Array<UserAssignedToRole> | undefined;
  lazyParams: LazyParams;
  setLazyParams: Dispatch<SetStateAction<LazyParams>>;
  pagination: PageInfo;
  isLoading: boolean;
  roleLocation: string;
  roleId: string;
}

const UsersAssignedToRoleList = ({
  lazyParams,
  users,
  pagination,
  isLoading,
  setLazyParams,
  roleLocation,
  roleId,
}: UsersAssignedToRoleListProps) => {
  const { t } = useTranslation();

  const [showSlider, setShowSlider] = useState<boolean>(false);
  const [selected, setSelected] = useState<RevokeLocationRolesInput[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  useEffect(() => {
    pagination.totalElements && setTotalRecords(pagination.totalElements);
  }, [pagination]);

  const canAssignRole = Permissions.IsAllowed(
    [Permissions.canAssignRole],
    UsersService.permissions
  );

  const { mutations } = useLocationsRolesMutation(roleLocation);

  const bulkActions = (): any[] => {
    return [
      {
        label: t("assigned_users_action_unassign"),
        command: confirmUnassign,
        disabled: selected.length === 0,
      },
      {
        label: t("assigned_users_action_assign"),
        command: () => setShowSlider(true),
      },
    ];
  };

  const confirmUnassign = () => {
    confirmDialog({
      message: t("user_roles_unassign_confirm_message"),
      header: t("user_roles_unassign_confirm_header"),
      accept: unassignRoles,
      reject: () => setSelected([]),
      acceptLabel: t("action_unassign"),
      rejectLabel: t("universal_Cancel"),
    });
  };

  const queryClient = useQueryClient();

  const unassignRoles = async () => {
    try {
      await mutations.mutate(LocationRolesActions.REVOKE, selected);
      queryClient.invalidateQueries({
        queryKey: ["usersAssignedToRole"],
      });
      queryClient.invalidateQueries({
        queryKey: ["infiniteUsersAssignedToRole"],
      });
      queryClient.invalidateQueries({
        queryKey: ["infiniteUsers"],
      });
    } catch {}
  };

  const assignRoles = async (values: { users: Record<string, string>[] }) => {
    const inputs: AssignLocationRolesInput[] = values.users.map((value) => ({
      userId: value.id,
      customerId: UsersService.customer.id,
      locationIds: [roleLocation],
      roleId,
    }));
    try {
      await mutations.mutate(LocationRolesActions.ASSIGN, inputs);
      queryClient.invalidateQueries({
        queryKey: ["usersAssignedToRole"],
      });
      queryClient.invalidateQueries({
        queryKey: ["infiniteUsersAssignedToRole"],
      });
      queryClient.invalidateQueries({
        queryKey: ["infiniteUsers"],
      });
    } catch {}
  };

  const handleGlobalSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setLazyParams((previous) => ({
        ...previous,
        filters: {
          ...previous.filters,
          displayName: {
            value: event.target.value,
          },
        },
      })),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    []
  );

  const onStatusFilter = (
    filterValue: Array<"ACTIVE" | "INACTIVE" | "PENDING">
  ): void => {
    setLazyParams((state: any) => ({
      ...state,
      filters: {
        ...state.filters,
        userStatus: {
          value: filterValue,
        },
      },
    }));
  };

  const onDirectlyAssignedFilter = (filterValue: string[]): void => {
    const value = filterValue.map((val) => {
      return ExplicitAssignedOptions.find((op) => op.id === val);
    });

    setLazyParams((state: any) => ({
      ...state,
      filters: {
        ...state.filters,
        isExplicitlyAssigned: {
          value: value,
        },
      },
    }));
  };

  const StatusBody = (rowData: UserAssignedToRole): React.ReactElement => {
    const cx = classNames.bind(classes);

    return (
      <div className={classes.status}>
        <div
          className={cx(classes.statusBadge, {
            isActive: rowData.userStatus === "ACTIVE",
            isPending: rowData.userStatus === "PENDING",
          })}
        ></div>
        <span className={classes.statusLabel}>
          {rowData.userStatus?.toLowerCase()}
        </span>
      </div>
    );
  };

  return (
    <div>
      <h2>{t("assigned_users_page_title")}</h2>
      <Card showHeader={false} full>
        <Table
          data={users}
          params={lazyParams}
          totalRecords={totalRecords}
          onSelectionChange={(e) => setSelected(e.value)}
          selectionMode="multiple"
          selection={selected}
          bulkActions={canAssignRole ? bulkActions : null}
          useBulkActionButtons
          onPage={setLazyParams}
          onSort={setLazyParams}
          isLoading={isLoading}
          fullPage={false}
          onGlobalSearch={handleGlobalSearch}
          dataKey="userID"
          testId="users-assigned-to-role-table"
        >
          <Column
            field="displayName"
            header={
              <span className={classes.capitalize}>
                {t("universal_display_name")}
              </span>
            }
            sortable
            body={(rowData: { displayName: string }) => (
              <span>{rowData?.displayName}</span>
            )}
          />
          <Column
            field="locationName"
            header={t("universal_assigned_location")}
          />
          {AdvancedFilterColumn({
            field: "isExplicitlyAssigned",
            filterField: "isExplicitlyAssigned",
            header: t("role_management_directly_assigned"),
            options: ExplicitAssignedOptions,
            body: (rowData) => (
              <span>{rowData.isExplicitlyAssigned ? "Yes" : "No"}</span>
            ),
            onApply: onDirectlyAssignedFilter,
            filterParams: lazyParams.filters,
          })}
          <Column field="email" header={t("universal_Email")} />
          {AdvancedFilterColumn({
            field: "userStatus",
            filterField: "userStatus",
            header: t("universal_Status"),
            options: UserStatusOptions,
            body: StatusBody,
            onApply: onStatusFilter,
            filterParams: lazyParams.filters,
          })}
        </Table>
      </Card>
      <LocationRoleAssignSlider
        showSlider={showSlider}
        assignRoleCallback={assignRoles}
        closeSliderCallback={() => setShowSlider(false)}
      />
    </div>
  );
};

export { UsersAssignedToRoleList };
